import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorMessage } from '@/shared/utils/error';

import {
  aguardarExportacaoPronta,
  FalhaDaExportacao,
} from '@/shared/utils/aguardarExportacaoPronta';

import type {
  AssinaturasConsolidadasResponse,
  ConsultaAssinaturas,
  ConsultaDeOpcoes,
  EstadoDaFeatureAssinaturas,
  ErroDaConsulta,
  FalhaDoExport,
  OpcoesDeFiltro,
  PedidoDeExportacao,
  PedidoDeParticipacoes,
  PaginaDeParticipacoes,
  ResultadoDaExportacao,
} from '../types/negociacaoAssinaturasTypes';
import { descartarArquivoParaBaixar, guardarArquivoParaBaixar } from '../utils/arquivoParaBaixar';
import { consultaDoDetalhe } from '../utils/consultaAssinaturas';
import { abrirArquivoEmNovaAba } from '../utils/exportacaoAssinaturas';
import { noEscritorioAtual, relancarCancelamento } from '../utils/noEscritorioAtual';
import { FalhaDasAssinaturas, negociacaoAssinaturasService } from './services';

const SEM_CONEXAO = 'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.';
const ERRO_DO_EXPORT = 'Erro ao exportar assinaturas';

/** Falha de rede e "Sessão expirada" chegam como `Error` comum: status 0, "nem chegou ao core". */
const paraErroDaConsulta = (erro: unknown): ErroDaConsulta =>
  erro instanceof FalhaDasAssinaturas
    ? erro.erro
    : { status: 0, mensagem: getErrorMessage(erro, SEM_CONEXAO) };

const statusDoErro = (erro: unknown): number | undefined =>
  erro instanceof FalhaDasAssinaturas ? erro.erro.status : undefined;

/** Depois do aceite do core: o job falhou, o prazo esgotou, a URL veio inválida ou o status caiu. */
const falhaDaEspera = (erro: unknown, exportId: string): FalhaDoExport => ({
  mensagem: getErrorMessage(erro, ERRO_DO_EXPORT),
  motivo: erro instanceof FalhaDaExportacao ? erro.motivo : 'consulta_do_status',
  exportId,
  status: statusDoErro(erro),
});

export const consultarAssinaturas = createAsyncThunk<
  AssinaturasConsolidadasResponse,
  ConsultaAssinaturas,
  { rejectValue: ErroDaConsulta }
>('negociacaoAssinaturas/consultar', async (consulta, { rejectWithValue }) => {
  try {
    return await noEscritorioAtual(() => negociacaoAssinaturasService.consultar(consulta));
  } catch (erro) {
    relancarCancelamento(erro);
    return rejectWithValue(paraErroDaConsulta(erro));
  }
});

export const consultarAssinaturasDaNegociacao = createAsyncThunk<
  AssinaturasConsolidadasResponse,
  string,
  { rejectValue: ErroDaConsulta }
>('negociacaoAssinaturas/consultarDetalhe', async (casoId, { rejectWithValue }) => {
  try {
    return await noEscritorioAtual(() =>
      negociacaoAssinaturasService.consultar(consultaDoDetalhe(casoId)),
    );
  } catch (erro) {
    relancarCancelamento(erro);
    return rejectWithValue(paraErroDaConsulta(erro));
  }
});

export const consultarOpcoesDeFiltro = createAsyncThunk<
  OpcoesDeFiltro,
  ConsultaDeOpcoes | string,
  { rejectValue: string }
>('negociacaoAssinaturas/consultarOpcoesDeFiltro', async (pedido, { rejectWithValue }) => {
  const { busca, page } = typeof pedido === 'string' ? { busca: pedido, page: 1 } : pedido;
  try {
    return await noEscritorioAtual(() =>
      page && page > 1
        ? negociacaoAssinaturasService.consultarOpcoesDeFiltro(busca, page)
        : negociacaoAssinaturasService.consultarOpcoesDeFiltro(busca),
    );
  } catch (erro) {
    relancarCancelamento(erro);
    return rejectWithValue(getErrorMessage(erro, 'Não foi possível carregar as opções de filtro'));
  }
});

export const carregarMaisParticipacoes = createAsyncThunk<
  PaginaDeParticipacoes,
  PedidoDeParticipacoes,
  { rejectValue: string }
>('negociacaoAssinaturas/carregarMaisParticipacoes', async (pedido, { rejectWithValue }) => {
  try {
    return await noEscritorioAtual(() =>
      negociacaoAssinaturasService.consultarParticipacoes(pedido),
    );
  } catch (erro) {
    relancarCancelamento(erro);
    return rejectWithValue(getErrorMessage(erro, 'Não foi possível carregar mais pessoas'));
  }
});

/**
 * Só o resultado (quantidades, id e se a aba abriu) vai ao estado. A URL assinada é aberta aqui e
 * não volta no `payload`: é credencial temporária e não pode ser serializada no Redux. Se o
 * navegador bloquear a aba, ela fica em `arquivoParaBaixar`, fora do Redux, para o "Baixar
 * arquivo". A falha leva o `exportId` assim que o core aceita o pedido: é o que liga o evento do
 * RUM ao log do worker.
 */
export const exportarAssinaturas = createAsyncThunk<
  ResultadoDaExportacao,
  PedidoDeExportacao,
  { rejectValue: FalhaDoExport; state: EstadoDaFeatureAssinaturas }
>(
  'negociacaoAssinaturas/exportar',
  async ({ chave, corpo }, { rejectWithValue, getState, requestId }) => {
    descartarArquivoParaBaixar(chave);
    if (typeof window === 'undefined') {
      return rejectWithValue({
        mensagem: 'Exportação disponível apenas no navegador',
        motivo: 'inicio',
      });
    }

    let exportId: string | undefined;
    try {
      return await noEscritorioAtual(async (confirmarContexto) => {
        const conferir = () => {
          confirmarContexto();
          if (getState().negociacaoAssinaturas.exportacoes[chave]?.consultaVigente !== requestId) {
            throw new DOMException('A exportação não pertence mais à tela atual', 'AbortError');
          }
        };
        ({ exportId } = await negociacaoAssinaturasService.iniciarExportacao(corpo));
        conferir();
        const idDoExport = exportId;
        const pronta = await aguardarExportacaoPronta((signal) => {
          conferir();
          return negociacaoAssinaturasService.consultarExportacao(idDoExport, signal);
        });
        conferir();
        const abriu = abrirArquivoEmNovaAba(pronta.signedUrl);
        if (!abriu) guardarArquivoParaBaixar(chave, pronta.signedUrl);
        return {
          exportId: idDoExport,
          formato: corpo.format,
          truncated: pronta.truncated,
          aberturaBloqueada: !abriu,
          linhasExportadas: pronta.linhasExportadas,
          linhasElegiveis: pronta.linhasElegiveis,
          limite: pronta.limite,
        };
      });
    } catch (erro) {
      relancarCancelamento(erro);
      return rejectWithValue(
        exportId
          ? falhaDaEspera(erro, exportId)
          : {
              mensagem: getErrorMessage(erro, ERRO_DO_EXPORT),
              motivo: 'inicio',
              status: statusDoErro(erro),
            },
      );
    }
  },
);
